import { useAnalytics, withEventContext } from "@codat/analytics";
import {
  ArrowThinDownIcon,
  ColumnDefinition,
  DataTable,
  IndicatorLabel,
  Typography,
  ZeroState,
} from "@codat/orchard-ui";
import { DataSet } from "api/models/DataSet";
import { PagedResponse } from "api/shared";
import { usePublicService } from "contexts/PublicServiceContext";
import PauseIcon from "icons/PauseIcon";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Observable } from "rxjs";
import { formatDate, getTimeToComplete } from "utils/timeFormaters";

import IdCell from "components/IdCell";
import PaginationRange from "components/PaginationRange";
import ValidationErrorCell from "components/ValidationErrorCell";

import styles from "./PullHistory.module.scss";
import { StateIndicatorLabel } from "./StateIndicatorLabel";
import ValidationErrorModal from "./ValidationErrorModal";
import ConnectionErrorBanner from "components/ConnectionErrorBanner";

const pageName = "Read history";

const DatatypeCell: React.FC<{
  value: { datatype: string; connectionPlatformName: string };
}> = ({ value: { datatype, connectionPlatformName } }) => {
  return (
    <div className={styles.cell}>
      <Typography as="p" variant="body_md">
        {datatype}
      </Typography>
      <Typography as="p" variant="body_xs" data-testid="platform-name">
        {connectionPlatformName}
      </Typography>
    </div>
  );
};

const RequestedDateCell: React.FC<{ value: { requested: string } }> = ({
  value: { requested },
}) => {
  return (
    <div className={styles.cell}>
      <Typography as="p" variant="body_md">
        {formatDate(requested)}
      </Typography>
    </div>
  );
};

const TimeToCompleteCell: React.FC<{
  value: { requested: string; completed: string };
}> = ({ value: { requested, completed } }) => {
  return (
    <div className={styles.cell}>
      <Typography as="p" variant="body_md">
        {getTimeToComplete(
          Date.parse(requested),
          completed ? Date.parse(completed) : null
        )}
      </Typography>
    </div>
  );
};

const StateCell: React.FC<{
  value: { state: string; description?: string; isPaused: boolean };
}> = ({ value: { state, description, isPaused } }) => {
  const label = isPaused ? (
    <IndicatorLabel label={state} leftIcon={<PauseIcon />} />
  ) : (
    <StateIndicatorLabel state={state} />
  );
  const statusDescription = description ? (
    <Typography
      as="p"
      variant="body_xs"
      className={styles.statusDescription}
      data-testid="status-description"
    >
      {description}
    </Typography>
  ) : null;
  return (
    <div className={styles.cell}>
      {label}
      {statusDescription}
    </div>
  );
};

interface PullHistoryProps extends JSX.IntrinsicAttributes {
  queueSync$?: Observable<void>;
}

const PullHistory = ({ queueSync$ }: PullHistoryProps) => {
  const { sendPageViewEvent } = useAnalytics();
  const { search } = useLocation();
  const history = useHistory();
  const [dataHistoryResult, setDataHistoryResult] =
    useState<PagedResponse<DataSet>>();
  const [dataHistoryError, setDataHistoryError] = useState();
  const [dataHistoryIsLoading, setDataHistoryIsLoading] = useState(true);
  const [dataConnectionError, setDataConnectionError] = useState(false);

  const connectionIdNameMap = useRef<Record<string, string>>({});

  function getNextPollDelay(currentDelay: number): number {
    return currentDelay < 30000 ? currentDelay * 2 : currentDelay;
  }

  const pageQuery = Number(new URLSearchParams(search).get("page"));

  const [currentPage, setCurrentPage] = useState(
    pageQuery && pageQuery > 0 ? pageQuery : 1
  );
  const { companyId } = useParams<{ companyId: string }>();
  const { getConnection, getDataHistory } = usePublicService();
  const pageSize = 50;

  const [validationErrorModal, setValidationErrorModal] = useState<{
    open: boolean;
    datasetId?: string;
    datatype?: string;
    requestedAt?: string;
    url?: string;
  }>({ open: false });

  if (!companyId) {
    throw new Error("No companyId provided.");
  }

  useEffect(() => {
    sendPageViewEvent(pageName);
  }, [sendPageViewEvent]);

  function transformDataSet(dataSet?: DataSet[]) {
    if (!dataSet) return [];

    return dataSet.map((row) => ({
      data: [
        {
          key: "type",
          value: {
            datatype: row.dataType,
            connectionPlatformName:
              connectionIdNameMap.current[row.connectionId],
          },
        },
        {
          key: "id",
          value: {
            id: row.id,
            name: "Dataset ID",
            copySuccessMessage: "Copied dataset ID to the clipboard",
            clipboardClassName: styles.hover,
            ["data-testid"]: "pull-history-dataset-id",
          },
        },
        {
          key: "requestedDate",
          value: { requested: row.requested },
        },
        {
          key: "timeToComplete",
          value: {
            requested: row.requested,
            completed: row.completed,
          },
        },
        {
          key: "status",
          value: {
            state: row.status,
            description: row.statusDescription,
            isPaused: row.isPaused,
          },
        },
        {
          key: "validationError",
          value: {
            companyId,
            datasetId: row.id,
            onClick: row.validationinformationUrl
              ? () =>
                  setValidationErrorModal({
                    open: true,
                    datasetId: row.id,
                    datatype: row.dataType,
                    requestedAt: row.requested,
                    url: row.validationinformationUrl,
                  })
              : null,
          },
        },
      ],
    }));
  }

  const poller = useCallback(
    (delay = 2000) => {
      getDataHistory(companyId, currentPage, pageSize)
        .then((response) => {
          setDataHistoryResult(response);

          const promises = response.results
            .reduce((ids, r) => {
              if (
                !connectionIdNameMap.current[r.connectionId] &&
                ids.indexOf(r.connectionId) < 0
              ) {
                ids.push(r.connectionId);
              }
              return ids;
            }, [] as string[])
            .map((id) => {
              return getConnection(companyId, id)
                .then((res) => {
                  connectionIdNameMap.current[id] = res.platformName;
                  setDataConnectionError(
                    (d) =>
                      d ||
                      (res.dataConnectionErrors &&
                        res.dataConnectionErrors.filter(
                          (x) => x.status === "Active"
                        ).length > 0)
                  );
                })
                .catch((err) => {
                  if (
                    err.response.request.status === 404 &&
                    err.response.data.error.includes(
                      `Data connection ${id} not found`
                    )
                  ) {
                    connectionIdNameMap.current[id] = "-";
                  }
                });
            });

          if (response.results.some((r) => !r.isCompleted)) {
            setTimeout(() => poller(getNextPollDelay(delay)), delay);
          }

          Promise.all(promises)
            .then(() => {
              setDataHistoryIsLoading(false);
            })
            //eslint-disable-next-line no-console
            .catch((err) => console.error(err));
        })
        .catch((e) => {
          setDataHistoryError(e);
          setDataHistoryIsLoading(false);
        });
    },
    [companyId, currentPage, getDataHistory, getConnection]
  );

  const reloadTable = useCallback(() => {
    setDataHistoryIsLoading(true);
    poller();
  }, [poller]);

  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  useEffect(() => {
    const subscription = queueSync$?.subscribe({
      next: () => {
        reloadTable();
      },
    });
    return () => subscription?.unsubscribe();
  }, [queueSync$, reloadTable]);

  const totalPages = Math.ceil(
    (dataHistoryResult?.totalResults ?? 1) / pageSize
  );

  const transformedData = transformDataSet(dataHistoryResult?.results);

  const columnDefinitions: ColumnDefinition<string>[] = [
    {
      key: "type",
      label: "Type",
      component: DatatypeCell,
    },
    {
      key: "id",
      label: "ID",
      component: IdCell,
    },
    {
      key: "requestedDate",
      label: "Requested date",
      component: RequestedDateCell,
    },
    {
      key: "timeToComplete",
      label: "Time to complete",
      component: TimeToCompleteCell,
    },
    {
      key: "status",
      label: "Status",
      component: StateCell,
    },
    {
      key: "validationError",
      label: "",
      component: ValidationErrorCell,
    },
  ];

  return (
    <div>
      <Typography
        as="h2"
        variant="heading_xxl"
        data-testid="pull-history-title"
      >
        Read history
      </Typography>
      {dataConnectionError && <ConnectionErrorBanner companyId={companyId} />}
      {dataHistoryError ? (
        <ZeroState
          data-testid="pull-history-zero-state"
          title="Error loading data, please try again."
          ctaText="Reload data"
          onCtaClick={window.location.reload}
          className={styles.zeroState}
        />
      ) : (
        <>
          {dataHistoryResult?.totalResults === 0 ? (
            <ZeroState
              className={styles.zeroState}
              icon={<ArrowThinDownIcon />}
              title="No read operations"
              description="Read operation history will appear here"
            />
          ) : (
            <>
              <DataTable
                className={styles.dataTable}
                data-testid="pull-history-table"
                rowsInfo={transformedData}
                columnDefs={columnDefinitions}
                isSkeleton={dataHistoryIsLoading}
                skeletonRows={pageSize}
                isLayoutAuto={true}
                hasBorder
              />
              <PaginationRange
                page={currentPage}
                total={totalPages}
                onUpdatePageIndex={(page) => {
                  history.push({
                    pathname: location.pathname,
                    search: `?page=${page}`,
                  });
                  setCurrentPage(page);
                }}
                isPrevNext
                className={styles.paginationNumbers}
                pageSize={pageSize}
              />
            </>
          )}
          {validationErrorModal.open && (
            <ValidationErrorModal
              companyId={companyId}
              datasetId={validationErrorModal.datasetId}
              datatype={validationErrorModal.datatype}
              requestedAt={validationErrorModal.requestedAt}
              validationErrorUrl={validationErrorModal.url}
              onClose={() => {
                setValidationErrorModal({ open: false });
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default withEventContext<PullHistoryProps>(PullHistory, pageName);
